import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import { useState } from "react";

type TableColumnMeta = {
  className?: string;
  headerClassName?: string;
  footerClassName?: string;
};

interface Props<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  initialSorting?: SortingState;
  getRowId?: (originalRow: T, index: number) => string;
  onRowClick?: (row: T) => void;
  rowClassName?: string | ((row: Row<T>) => string);
  tableClassName?: string;
  theadClassName?: string;
  tbodyClassName?: string;
  showFooter?: boolean;
  footerRowClassName?: string;
}

export function Table<T>({
  columns,
  data,
  initialSorting = [],
  getRowId,
  onRowClick,
  rowClassName,
  tableClassName,
  theadClassName,
  tbodyClassName,
  showFooter = false,
  footerRowClassName,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const getMeta = (meta: unknown) => meta as TableColumnMeta | undefined;

  return (
    <table
      className={classNames(
        "w-full table-auto border-collapse font-Manrope",
        tableClassName,
      )}>
      <thead
        className={classNames(
          "border-b border-[#E4E9F2] text-left text-sm font-medium text-[#5F758F]",
          theadClassName,
        )}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const canSort = header.column.getCanSort();
              const meta = getMeta(header.column.columnDef.meta);

              return (
                <th
                  key={header.id}
                  onClick={
                    canSort
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                  className={classNames(
                    "p-4",
                    canSort && "cursor-pointer select-none",
                    meta?.headerClassName,
                  )}>
                  <div className={"flex items-center gap-1"}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}

                    {canSort && (
                      <span className={"text-xs"}>
                        {{
                          asc: "↑",
                          desc: "↓",
                        }[header.column.getIsSorted() as string] ?? ""}
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        ))}
      </thead>

      <tbody
        className={classNames(
          "text-sm font-normal text-[#3F5266]",
          tbodyClassName,
        )}>
        {table.getRowModel().rows.map((row) => {
          const resolvedRowClassName =
            typeof rowClassName === "function"
              ? rowClassName(row)
              : rowClassName;

          return (
            <tr
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              className={classNames(
                "border-b border-[#E4E9F2] last:border-none",
                onRowClick && "cursor-pointer",
                resolvedRowClassName,
              )}>
              {row.getVisibleCells().map((cell) => {
                const meta = getMeta(cell.column.columnDef.meta);

                return (
                  <td
                    key={cell.id}
                    className={classNames("p-4", meta?.className)}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>

      {showFooter && (
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id} className={classNames(footerRowClassName)}>
              {footerGroup.headers.map((footer) => {
                const meta = getMeta(footer.column.columnDef.meta);

                return (
                  <td
                    key={footer.id}
                    colSpan={footer.colSpan}
                    className={classNames(
                      "p-4",
                      meta?.footerClassName ?? meta?.className,
                    )}>
                    {footer.isPlaceholder
                      ? null
                      : flexRender(
                          footer.column.columnDef.footer,
                          footer.getContext(),
                        )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tfoot>
      )}
    </table>
  );
}
